export const meta = {
  id: '17-negation',
  order: 17,
  titleFi: 'Kielto',
  titleEn: 'Negation',
  duolingoUnit: 'duo-19-negation',
  // + Sanapaja's own words this lesson first needs (docs/COURSE.md "Own words in the lessons"; waves/wave-NN/lessons.json)
  lemmaIds: ['daor', 'nevegon', 'bardugon', 'ours:kempēgremagon', 'ours:pirtiapuragon', 'ours:mīvūndegon', 'ours:hēgekagon', 'ours:suenūljagon', 'ours:aerittūljagon', 'ours:qrīdrakegon', 'ours:mōzutir', 'ours:averillāzma', 'ours:mōzukio', 'ours:jojovagon', 'ours:kisībītsos', 'ours:ñāqiapos', 'ours:pitsa', 'ours:tabaki', 'ours:narkotiki', 'ours:kokaini', 'ours:heroini', 'ours:lōdrilla', 'ours:porni', 'ours:dorajorrāelta', 'ours:gumi', 'ours:sigareti'],
  exampleIds: ['got-s3e04-006', 'ex-a708e8ea0159', 'got-s7e07-005', 'hotd-s1e06-017', 'ex-e61df45a8982', 'ex-9a162ed0f47b', 'ex-f8f6079cdc44', 'ex-01d1b37f1cda'],
  checkFocus: { mood: ['subjunctive'] },   // what the end-of-lesson check tests first
  sources: [
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#Negation',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar#Subjunctive',
    'https://wiki.languageinvention.com/index.php?title=User:Najahho/High_Valyrian_Verb_Moods#Negative_statements',
    'https://wiki.languageinvention.com/index.php?title=Daor',
    'https://wiki.languageinvention.com/index.php?title=Idakogon',
  ],
  // the week (docs/COURSE.md "Lesson hub")
  grammar: ['subjunctive', 'negation'],
  drills: { mood: ['subjunctive'] },
  showLines: ['got-s3e04-006', 'got-s7e07-005', 'hotd-s1e06-017'],
  pronunciation: { focus: 'diphthongs', words: ['daor', 'gevie', 'oktion', 'āeksio', 'aerikagon'], lines: ['got-s3e04-006', 'got-s7e07-005'] },
  letter: {
    fi: 'Kirjoita kolme asiaa, joita et tee (daor).',
    en: 'Write three things you do not do (daor).',
  },
  scene: 'hotd-s1e06-1',
}

# Kielto

Tähän asti olet sanonut valyriaksi lähinnä sen, mikä on totta: näen, kuulen, syön. Olla-verbin kaksi kielteistä ilmausta opit jo <LessonLink n={3}>oppitunnilla 3</LessonLink>, ja nyt opit kieltämään minkä tahansa verbin. Kieltolause on rakenteeltaan yksinkertainen, mutta sen mukana tulee uusi verbimuoto, **subjunktiivi**. Hyvä uutinen suomalaiselle: meidänkin kielessämme verbi muuttuu, kun se kielletään.

Aloitetaan lauseella, jonka Daenerys sanoo kahdesti, kolmannella ja seitsemännellä tuotantokaudella:

<Line id="got-s3e04-006" />

## Daor lauseen loppuun – ja verbi vaihtaa muotoa

Kieltosana on <Hv glossed>daor</Hv>, jonka olet jo nähnyt vastauksena ’ei’. Tavallinen kielto tehdään yksinkertaisesti lisäämällä <Hv>daor</Hv> **lauseen loppuun**. Ainoa juju on, että samalla verbi vaihtuu **indikatiivista subjunktiiviin**. Duolingon Tips & Notesin vertailupari:

- <Hv glossed>Hontī rȳban.</Hv> ’Kuulen linnut.’
- <Hv glossed>Hontī rȳbon daor.</Hv> ’En kuule lintuja.’

<Interlinear hv="Hont|ī rȳb|on daor." gloss="lintu-ACC.PL kuulla-PRS.SBJV.1SG ei" fi="En kuule lintuja." />

Suomalainen tuntee ilmiön. Myös suomessa pääverbi muuttuu kiellossa: *kuulen* → *en kuule*. Suomessa pääverbi on kieltomuodossa ja persoona siirtyy kieltoverbiin (*en, et, ei, emme, ette, eivät*). Valyriassa työnjako on toisin päin: <Hv>daor</Hv> ei taivu koskaan, ja persoona pysyy verbissä, joka saa subjunktiivin päätteen.

<Compare fi="kuul|en" hv="rȳb|an" label="myönteinen: indikatiivi" />

<Compare glossed fi="en kuul|e" hv="rȳb|on daor" label="kielteinen: kieltomuoto ~ subjunktiivi" />

Daenerysin repliikissä myönteinen ja kielteinen lause ovat peräkkäin – sama verbi, kaksi muotoa:

<Example id="ex-a708e8ea0159" />

<Hv glossed>Maghon daor</Hv> ’en tuo’ on subjunktiivi, <Hv glossed>maghan</Hv> ’tuon’ indikatiivi. Kieltolauseen tunnistat kahdesta asiasta: lauseen lopussa on <Hv>daor</Hv>, ja verbin päätteessä on <Hv>o</Hv>.

Mitä subjunktiivi oikeastaan tarkoittaa? Duolingon ohjeiden mukaan sitä ei tarvitse vielä murehtia. Wikin kielioppisivun mukaan subjunktiivi on kuviteltujen, epävarmojen ja epätodellisten asioiden tapaluokka, mutta ylivoimaisesti yleisimmin sitä käytetään juuri kiellossa <Hv>daor</Hv>-sanan kanssa. Tässä vaiheessa riittää muistaa: **kieltolause = subjunktiivi + <Hv>daor</Hv>**.

## Subjunktiivin preesens

Subjunktiivin persoonapäätteet ovat tuttuja, sillä ne noudattavat samaa kaavaa kuin preesensin päätteet. Ero on <Term id="teemavokaali">teemavokaalissa</Term>: <Term id="juuri">juuren</Term> perään tulee <Hv>o</Hv>.

**Konsonanttiin päättyvät juuret** saavat päätteet <Hv>-on</Hv>, <Hv>-ō</Hv>, <Hv>-os</Hv>, <Hv>-oty</Hv>, <Hv>-ōt</Hv>, <Hv>-osy</Hv>:

<Paradigm lemma="rȳbagon" filter="subjunctive.active.present" numbers="sg,pl" />

**<Hv>a</Hv>:han päättyvät juuret** saavat täsmälleen samat päätteet, ja juuren <Hv>a</Hv> säilyy: <Hv>ȳdraon</Hv>, <Hv>ȳdraō</Hv>, <Hv>ȳdraos</Hv>.

<Paradigm lemma="ȳdragon" filter="subjunctive.active.present" numbers="sg,pl" />

**<Hv>e</Hv>:hen tai <Hv>i</Hv>:hin päättyvissä juurissa** juuren loppuvokaali muuttuu <Hv>i</Hv>:ksi, ja sen perään tulevat tavalliset päätteet. Tämän oppitunnin <Hv glossed>nevegon</Hv> ’kantaa’ on tällainen:

<Paradigm lemma="nevegon" filter="subjunctive.active.present" numbers="sg,pl" />

**<Hv>o</Hv>:hon tai <Hv>u</Hv>:hun päättyvissä juurissa** loppuvokaali muuttuu <Hv>v</Hv>:ksi. Tämän oppitunnin <Hv glossed>bardugon</Hv> ’kirjoittaa’ on tällainen:

<Paradigm lemma="bardugon" filter="subjunctive.active.present" numbers="sg,pl" />

Duolingon ohjeet muistuttavat samasta asiasta kuin preesensin yhteydessä: erota toisistaan konsonanttiin ja <Hv>a</Hv>:han päättyvät juuret. Infinitiivistä eroa ei näe – <Hv>rȳbagon</Hv> ja <Hv>ȳdragon</Hv> näyttävät samanlaisilta – eikä preesensin yksikön 1. persoonastakaan, mutta monikon 3. persoonasta näkee, mihin juuri päättyy: <Seam glossed s="rȳb" e="is" /> ’he kuulevat’ mutta <Seam glossed s="ȳdra" e="si" /> ’he puhuvat’. Siksi subjunktiivissa on <Seam s="rȳb" e="on" /> mutta <Seam s="ȳdr" e="aon" />.

<Note>
Duolingon tehtävissä voi yhä näkyä kirjoitusasu *idakagon* (kurssin vuoden 2019 sanalistassa se on). Oikein on <Hv>idakogon</Hv>: niin korjaavat Duolingon omat ohjeet tässä yksikössä, ja niin on wikin sanakirjassa. Sen juuri päättyy <Hv>o</Hv>:hon, joten se käyttäytyy kuten <Hv>bardugon</Hv>: <Hv glossed>idakvon daor</Hv> ’en hyökkää’.
</Note>

## Olla – tärkein kaikista

Verbi <Hv glossed>sagon</Hv> ’olla’ on epäsäännöllinen, ja sen subjunktiivi kannattaa opetella sellaisenaan, koska ”ei ole” on yksi kielen yleisimmistä ilmauksista. Kaksi sen muotoa osaat jo <LessonLink n={3}>oppitunnilta 3</LessonLink>: <Hv glossed>iksos daor</Hv> ’ei ole’ (alun repliikissä) ja <Hv glossed>ikson daor</Hv> ’en ole’. Tässä on koko taulukko:

<Paradigm lemma="sagon" filter="subjunctive.active.present" numbers="sg,pl" />

<Interlinear hv="Zaldrīz|es buzdari iksos daor." gloss="lohikäärme-NOM orja-NOM olla-PRS.SBJV.3SG ei" fi="Lohikäärme ei ole orja." />

Daenerysin myöhemmän repliikin näit jo <LessonLink n={3}>oppitunnilla 3</LessonLink>: siinä myönteinen <Hv glossed>issa</Hv> ’on’ ja kielteinen <Hv glossed>iksos daor</Hv> ’ei ole’ ovat peräkkäisissä lauseissa:

<Line id="got-s7e07-005" />

Yksinään <Hv glossed>daor</Hv> on yksinkertaisesti ’ei!’, kuten tässä kirurgin vastauksessa:

<Line id="hotd-s1e06-017" />

<Note>
Wikin tapaluokkasivun mukaan subjunktiivin laukaisee nimenomaan sana <Hv>daor</Hv>. Muiden kieltävien sanojen, kuten ’ei mikään’ tai ’ei kukaan’, kanssa verbi näyttää olevan tavallisessa indikatiivissa:

<Example id="ex-e61df45a8982" />

Saman sivun mukaan kielteinen kysymys voi olla merkitykseltään myönteinen, aivan kuten suomen kohtelias *Etkö tulisi kahville?*. Käännöksessä kysytään ”nautitko”, mutta kirjaimellisesti lause kysyy ”etkö pidä”:

<Example id="ex-9a162ed0f47b" />
</Note>

## Daor äännetään kahtena tavuna

Kirjoitettuna <Hv>daor</Hv> näyttää yksitavuiselta, mutta wikin hakusanan mukaan se äännetään useimmiten kahtena erillisenä vokaalina, paino jälkimmäisellä tavulla: *da·OR*, ja Peterson sanoo itsekin usein näin. Syy on historiallinen: sana juontuu vanhentuneesta verbivartalosta, joka merkitsi ’kieltäytyä’ ja jonka paino oli useimmissa muodoissa vartalon viimeisellä tavulla. Alla oleva ääntämisohje näyttää ensin tämän tavallisimman ääntämyksen ja sen vieressä yksitavuisen, kirjoitusasun mukaisen ääntämyksen *daor*. Kumpikin on oikein.

<Pronounce word="daor" />

Tämän oppitunnin kaksi verbiä ovat hyvää painoharjoitusta. <Hv>Bardugon</Hv> painottuu ensimmäiselle tavulle, koska toiseksi viimeinen tavu *du* on kevyt ja sitä edeltävä *bar* raskas. <Hv>Nevegon</Hv> taas painottuu keskelle, koska ensimmäinen tavu *ne* on kevyt eikä vedä painoa puoleensa.

<Pronounce word="bardugon" />

<Pronounce word="nevegon" />

## Kantaa ja kirjoittaa

Verbin <Hv glossed>nevegon</Hv> ’kantaa’ kanssa määränpää on datiivissa, kuten edellisen oppitunnin liikeverbeillä:

<Example id="ex-f8f6079cdc44" />

Verbin <Hv glossed>bardugon</Hv> ’kirjoittaa’ kanssa datiivissa on se, **jolle** kirjoitetaan. Tämä esimerkki on imperatiivissa, johon palaamme pian. Huomaa datiivi <Hv glossed>mittot</Hv> ’typerykselle’:

<Example id="ex-01d1b37f1cda" />

## Harjoituslauseita

Nämä lauseet on koottu tämän oppitunnin säännöillä ja taivutustaulukoiden muodoilla. Ne eivät ole Petersonin lauseita. Jokaisessa on sama kaava: subjunktiivi ja lopussa <Hv>daor</Hv>.

<Practice hv="Havon nevion daor." fi="En kanna leipää." />

<Practice hv="Riña taobot bardvos daor." fi="Tyttö ei kirjoita pojalle." />

<Practice hv="Zaldrīzes hontī rȳbos daor." fi="Lohikäärme ei kuule lintuja." />

<Practice hv="Ābra drōmon iprados daor." fi="Nainen ei syö munaa." />

<Practice hv="Ȳdraosy daor." fi="He eivät puhu." />

## Sanat

<Vocab />

<Check />
